/**
 * The help page: every published FAQ entry, filterable.
 *
 * ── WHY `<details>` AND NOT A CUSTOM ACCORDION ──────────────────────────────
 * A native `<details>`/`<summary>` is keyboard-operable, exposes the right
 * role and expanded state to assistive technology, is findable by the
 * browser's own in-page search when open, and prints. A div with a click
 * handler is none of those until several dozen lines of ARIA make it so, and
 * this phase is structure, not decoration.
 *
 * ── WHY THE FILTER IS CLIENT-SIDE ───────────────────────────────────────────
 * `GET /cms/faq` returns every active entry in one unpaginated response — that
 * is core's shape, not a choice made here — so the entries are already in the
 * browser. Asking the server to filter a list it already sent would add a
 * round trip and a loading state for nothing. If the FAQ ever grows past a few
 * hundred entries this becomes the wrong answer and the endpoint needs a query.
 */
import { t } from '@/engine/i18n';
import { getSiteInfo } from '@/core/preview/preview';
import { fetchFaq, type FaqEntry } from './cms-api';
import { TAP_LINK, createPageFrame, el, mountAsyncPage, renderEmpty } from './page-parts';
import { applyProseClasses, renderRichText } from './rich-text';
import { decorateDisclosure } from '@/engine/landing/cta';
import { LEGAL_INDEX_HREF } from './legal-page';

/** Case- and accent-insensitive substring match over the question and answer. */
export function faqMatches(entry: FaqEntry, query: string): boolean {
  const needle = normalise(query);
  if (needle === '') return true;
  return normalise(`${entry.question} ${entry.answer}`).includes(needle);
}

function normalise(value: string): string {
  return value.toLowerCase().normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
}

export function renderFaqPage(root: HTMLElement): () => void {
  const doc = root.ownerDocument;
  const { main, body } = createPageFrame(doc, {
    title: t('faq.title'),
    subtitle: t('faq.subtitle'),
  });
  root.replaceChildren(main);

  return mountAsyncPage<FaqEntry[]>(body, {
    load: (signal) => fetchFaq({ signal }),
    notFound: { title: t('common.nothing_here'), body: t('faq.subtitle') },
    render: (entries, host) => {
      if (entries.length === 0) {
        // In a preview build core answers `[]` rather than 404, so an empty
        // list is the expected shape there too. `renderEmpty` says "nothing
        // here yet" either way, which is true in both cases.
        renderEmpty(host, t('common.nothing_here'));
        host.appendChild(renderStillStuck(doc));
        return;
      }

      const sorted = [...entries].sort((a, b) => a.sortOrder - b.sortOrder);

      const search = el(doc, 'div', 'mb-6');
      // A visible placeholder is not a label: it disappears the moment there
      // is text in the field, and it is not exposed as one.
      const label = el(doc, 'label', 'sr-only', t('faq.search_placeholder'));
      label.htmlFor = 'faq-search';
      const input = el(
        doc,
        'input',
        'w-full min-h-11 px-3 py-2 rounded border border-border bg-surface text-text ' +
          'focus:border-gold focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold',
      );
      input.id = 'faq-search';
      input.type = 'search';
      input.autocomplete = 'off';
      input.placeholder = t('faq.search_placeholder');
      search.appendChild(label);
      search.appendChild(input);

      const count = el(doc, 'p', 'mt-2 text-sm text-text-dim');
      // The count changes as the visitor types, so it is announced politely
      // rather than silently: a filter that removes everything must not leave
      // a screen-reader user staring at an unexplained empty page.
      count.setAttribute('role', 'status');
      count.setAttribute('aria-live', 'polite');
      search.appendChild(count);
      host.appendChild(search);

      const list = el(doc, 'div', 'divide-y divide-border border-y border-border');
      const rows = sorted.map((entry) => ({ entry, node: renderEntry(doc, entry) }));
      for (const row of rows) list.appendChild(row.node);
      host.appendChild(list);

      const empty = el(doc, 'p', 'py-8 text-text-dim');
      empty.hidden = true;
      host.appendChild(empty);
      host.appendChild(renderStillStuck(doc));

      const apply = (): void => {
        const query = input.value.trim();
        let visible = 0;
        for (const row of rows) {
          const shown = faqMatches(row.entry, query);
          row.node.hidden = !shown;
          if (shown) visible++;
        }
        count.textContent = t('faq.results_count', { count: visible });
        empty.hidden = visible !== 0;
        empty.textContent = visible === 0 ? t('faq.no_results', { query }) : '';
        list.hidden = visible === 0;
      };

      input.addEventListener('input', apply);
      apply();
    },
  });
}

function renderEntry(doc: Document, entry: FaqEntry): HTMLElement {
  const details = el(doc, 'details', 'py-1 group');
  const summary = el(
    doc,
    'summary',
    'flex items-center min-h-11 py-2 cursor-pointer text-text ' +
      'focus-visible:outline focus-visible:outline-2 focus-visible:outline-gold',
    entry.question,
  );
  decorateDisclosure(summary);
  details.appendChild(summary);

  const answer = el(doc, 'div', 'pb-3 pt-1 text-text-dim');
  renderRichText(answer, entry.answer, { externalLinkLabel: t('a11y.external_link'), doc });
  applyProseClasses(answer);
  details.appendChild(answer);
  return details;
}

/**
 * "Still stuck?" with a way to reach a person.
 *
 * The support address comes from `/site-info`, which is blank in a preview
 * build and may be unconfigured in a fresh deployment. Rather than render a
 * `mailto:` with nothing after it, the link falls back to the legal index —
 * which is where the operator's real contact details live.
 */
function renderStillStuck(doc: Document): HTMLElement {
  const box = el(doc, 'div', 'mt-10 pt-6 border-t border-border');
  box.appendChild(el(doc, 'h2', 'font-heading text-lg text-text mb-1', t('faq.still_stuck')));
  const email = getSiteInfo()?.company.email ?? '';
  const link = el(doc, 'a', TAP_LINK, t('faq.contact'));
  link.href = email === '' ? LEGAL_INDEX_HREF : `mailto:${email}`;
  box.appendChild(link);
  return box;
}
